import { teamMembers } from "@/data/TeamData";
import {
  CaseStudyMethodStep,
  CaseStudyScheduleRow,
  CaseStudyStat,
  CaseStudyWorking,
} from "@/lib/types";

export const caseStudyHeadline = ["Work", "we", "would", "show", "you"];
export const caseStudyHeadlineAccent = "first.";

export const caseStudyLede = [
  "Nothing on this page is a concept or a pitch mock. Every engagement below is live, instrumented and still reporting to a number its client checks — a fashion marketplace rebuilt around product discovery, a luxury fragrance storefront in Bangladesh, a SaaS video growth funnel and a cinematic content studio with a global roster.",
  "These are the ones we lead with because the outcome is measurable and the client will take the call. Read the row, then open it and read the working underneath.",
];

// Three reels behind the banner headline, fastest last.
export const caseStudyReels = [
  { words: "Selected work", repeat: 6, speed: 0.55, direction: 1 as const },
  {
    words: "Built · Shipped · Measured",
    repeat: 4,
    speed: 0.42,
    direction: -1 as const,
  },
  {
    words: "+40% · BDT 3M+ · 45+ · Global",
    repeat: 4,
    speed: 0.7,
    direction: 1 as const,
  },
];

export const caseStudyBannerStats: CaseStudyStat[] = [
  { value: "+40%", label: "Conversion lift — fashion marketplace rebuild" },
  {
    value: "BDT 3M+",
    label: "Revenue scaled on a single storefront",
    count: false,
  },
  { value: "45+", label: "Startups funnelled through one SaaS page" },
  {
    value: "Global",
    label: "Roster incl. Daymond John, Biohack Yourself",
    count: false,
  },
];

export const caseStudyWorkings: CaseStudyWorking[] = [
  {
    slug: "chic-charming",
    context: "Fashion marketplace",
    problem:
      "A respected fashion marketplace with a strong market reputation was losing shoppers between arriving and deciding. Product discovery carried friction, messaging was inconsistent across templates, and mobile optimisation was limited in a category that browses and buys almost entirely on phones.",
    actions: [
      "Research showed 70% of fashion shoppers prefer a minimal interface where product imagery leads.",
      "Cut click-depth between landing and decision; rebuilt the visual hierarchy around photography.",
      "Next.js architecture with optimised image delivery for near-instant loads across regions.",
      "Mobile-first layouts, so the primary device became the primary design target.",
      "Outcome: conversion rate up 40%, bounce down 25% inside the first month live.",
    ],
    verify:
      "Count the taps from landing to a chosen size on a phone — that path is the whole rebuild.",
  },
  {
    slug: "elorva-perfumes",
    context: "Luxury fragrance, Bangladesh",
    problem:
      "Elorva sells genuine Dior, Chanel, Tom Ford and Creed alongside niche houses like Initio Parfums, Xerjoff and Kilian — into a market saturated with counterfeits. Nothing in the previous presence proved the goods were real, and checkout did not match how the country actually pays.",
    actions: [
      "Deep green and gold identity, cinematic product photography, premium unboxing presentation.",
      "Filtering by brand, gender, decant size (3ml / 6ml / 15ml), full bottles, gift boxes and combos.",
      "Next.js with server-side rendering for fast first paint and clean indexable product pages.",
      "bKash, Nagad, Rocket and cash on delivery wired into a single checkout.",
      "Product-level JSON-LD and FAQ schema, GA4 and Pixel driving retargeting — 3× returning customers.",
    ],
    verify:
      "Open any bottle and start checkout: bKash, Nagad, Rocket and cash on delivery sit in one flow.",
  },
  {
    slug: "moson-media",
    context: "SaaS video growth engine",
    problem:
      "The SaaS video marketing category is crowded and every agency in it claims the same outcome. Moson's own page led with views and impressions — the vocabulary of a content supplier, not of a growth partner — while the founders were selling to buyers who budget against revenue.",
    actions: [
      "Repositioned around startups that need video driving real growth, not polished content.",
      "Messaging framework rebuilt on signups, conversions and revenue.",
      "React and Vite single-page build; coral palette and Outfit type for speed and readability.",
      "Structured data and social tags so organic traffic converts rather than only counting.",
      "Testimonials, client logos and embedded work placed at every hesitation point.",
    ],
    verify:
      "Time the first screen — how long before you know what they sell and who it is for?",
  },
  {
    slug: "artevic-media",
    context: "Cinematic content studio",
    problem:
      "Founded by Nur Alam Shakib and editing for Daymond John and Biohack Yourself, Artevic held a roster its website did not reflect. The tagline promised one agency, infinite editing possibilities; the site had to feel limitless and still convert a first-time visitor into a booked consultation.",
    actions: [
      "Dark cinematic interface built so the portfolio work leads every screen.",
      "Four service pillars — talking head, podcast and reels, SaaS promo, social strategy — each with its own portfolio route.",
      "Verified client testimonials placed immediately after the showcase, where the trust gap opens.",
      "Tiered pricing, Calendly consultations and WhatsApp messaging so no lead waits for a reply.",
    ],
    verify:
      "Follow any of the four service pillars through to its portfolio and back to a booking.",
  },
];

export const caseStudySchedule: CaseStudyScheduleRow[] = [
  {
    weeks: "WK 1–2",
    stage: "Strategy",
    cells: [
      {
        slug: "chic-charming",
        title: "Read the category",
        detail:
          "A marketplace losing shoppers before the product page ever loaded.",
      },
      {
        slug: "elorva-perfumes",
        title: "Beat the fakes",
        detail:
          "Authenticity taken as the brand position, not a footer disclaimer.",
      },
      {
        slug: "moson-media",
        title: "Change the pitch",
        detail:
          "Growth partner for SaaS, not a content supplier billing per edit.",
      },
      {
        slug: "artevic-media",
        title: "Match the roster",
        detail:
          "The site had to carry a Daymond John credit without flinching.",
      },
    ],
  },
  {
    weeks: "WK 2–4",
    stage: "Discovery",
    cells: [
      {
        slug: "chic-charming",
        title: "70% want imagery first",
        detail: "Research reset the hierarchy and the click-depth, not taste.",
        decisive: true,
      },
      {
        slug: "elorva-perfumes",
        title: "Trust gaps mapped",
        detail: "Every point where a buyer hesitates on a fragrance listing.",
      },
      {
        slug: "moson-media",
        title: "Buyer vocabulary",
        detail: "Signups, conversions and revenue — never impressions.",
      },
      {
        slug: "artevic-media",
        title: "Four real needs",
        detail: "Talking head, podcast and reels, SaaS promo, social strategy.",
      },
    ],
  },
  {
    weeks: "WK 4–7",
    stage: "Concept",
    cells: [
      {
        slug: "chic-charming",
        title: "Photography leads",
        detail: "Layouts composed around the product shot, then typeset.",
      },
      {
        slug: "elorva-perfumes",
        title: "Deep green and gold",
        detail: "Luxury signalled before a single word is read.",
        decisive: true,
      },
      {
        slug: "moson-media",
        title: "One page only",
        detail: "The whole funnel collapsed into a single deliberate scroll.",
      },
      {
        slug: "artevic-media",
        title: "Cinematic dark",
        detail: "The work itself becomes the interface.",
      },
    ],
  },
  {
    weeks: "WK 7–10",
    stage: "Refinement",
    cells: [
      {
        slug: "chic-charming",
        title: "Mobile-first passes",
        detail:
          "Slow networks and small screens broken on purpose, then fixed.",
      },
      {
        slug: "elorva-perfumes",
        title: "Checkout rails",
        detail: "bKash, Nagad, Rocket and cash on delivery in one flow.",
      },
      {
        slug: "moson-media",
        title: "Proof placement",
        detail: "Testimonials moved to the exact hesitation points.",
        decisive: true,
      },
      {
        slug: "artevic-media",
        title: "Three booking routes",
        detail: "Tiered pricing, Calendly, WhatsApp.",
      },
    ],
  },
  {
    weeks: "WK 10+",
    stage: "System",
    cells: [
      {
        slug: "chic-charming",
        title: "Next.js and image CDN",
        detail: "Performance owned by the architecture, not by a plugin.",
      },
      {
        slug: "elorva-perfumes",
        title: "JSON-LD and FAQ schema",
        detail: "Local search taken, then kept.",
      },
      {
        slug: "moson-media",
        title: "Structured data",
        detail: "Organic traffic converted rather than merely counted.",
      },
      {
        slug: "artevic-media",
        title: "Documented handover",
        detail:
          "Component library and analytics, so the studio ships without us.",
        decisive: true,
      },
    ],
  },
];

export const caseStudyReceipts: CaseStudyStat[] = [
  { value: "+40%", label: "Conversion lift — Chic & Charming" },
  { value: "−25%", label: "Bounce rate — first month live" },
  { value: "BDT 3M+", label: "Revenue scaled — Elorva Perfumes", count: false },
  { value: "45+", label: "Startups funnelled — Moson Media" },
];

export const caseStudyMethod: CaseStudyMethodStep[] = [
  {
    number: "01",
    title: "Strategy",
    description:
      "Category, competitors, and the single belief a buyer has to change. Written down and agreed before anything is designed, because a position invented after the layout never survives the build.",
  },
  {
    number: "02",
    title: "Discovery",
    description:
      "Click-depth, drop-off points, support tickets and recorded sessions. Research decides the hierarchy; preference gets to decide almost nothing.",
  },
  {
    number: "03",
    title: "Concept",
    description:
      "One direction taken far enough to judge properly, rather than three safe options diluted in review until nobody can tell them apart.",
  },
  {
    number: "04",
    title: "Refinement",
    description:
      "Slow networks, small screens, the worst product photo the client owns, empty states and error paths. Broken on purpose, fixed before launch.",
  },
  {
    number: "05",
    title: "System",
    description:
      "Component library, schema markup and analytics documented and handed over, so your team keeps shipping without booking us for every change.",
  },
];

// The same stack the team lists on their own profiles, deduped in profile
// order — one source of truth rather than a second hand-kept list.
export const caseStudyTechStrip = [
  ...new Set(teamMembers.flatMap((member) => member.techStack)),
];

export const caseStudyCloseFacts = [
  { label: "First reply", value: "One working day" },
  { label: "Opening scope", value: "Two-week diagnostic" },
  { label: "Studio", value: "Dhaka — global delivery" },
];
